import { useState, useEffect } from 'react';
import { Listing } from '@/types/listing';
import PhotoUploader from './PhotoUploader';

interface Props {
  listing?: Listing | null;
  onSave: (data: Omit<Listing, 'id'>) => void;
  onClose: () => void;
}

const PROPERTY_TYPES = ['Single Family', 'Condo', 'Luxury Estate', 'Beach House', 'Penthouse', 'Townhouse', 'Land'];
const STATUSES = ['Active', 'New', 'Pending', 'Sold'];

const makeEmpty = (): Omit<Listing, 'id'> => ({
  address: '',
  price: '',
  beds: 3,
  baths: 2,
  sqft: '',
  type: 'Single Family',
  status: 'Active',
  description: '',
  images: [],
  city: '',
});

export default function ListingFormModal({ listing, onSave, onClose }: Props) {
  const [form, setForm] = useState<Omit<Listing, 'id'>>(makeEmpty());
  const [errors, setErrors] = useState<Partial<Record<keyof Omit<Listing, 'id'>, string>>>({});

  useEffect(() => {
    if (listing) {
      const { id, ...rest } = listing;
      setForm({ ...rest, images: [...rest.images] });
    } else {
      setForm(makeEmpty());
    }
    setErrors({});
  }, [listing]);

  const set = <K extends keyof Omit<Listing, 'id'>>(key: K, value: Omit<Listing, 'id'>[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const e: Partial<Record<keyof Omit<Listing, 'id'>, string>> = {};
    if (!form.address.trim()) e.address = 'Address is required';
    if (!form.price.trim()) e.price = 'Price is required';
    if (!form.sqft.trim()) e.sqft = 'Sq ft is required';
    if (!form.description.trim()) e.description = 'Description is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">
            {listing ? 'Edit Listing' : 'Add New Listing'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-gray-500 text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Address *</label>
            <input
              type="text"
              value={form.address}
              onChange={(e) => set('address', e.target.value)}
              placeholder="e.g. 4821 Bayshore Blvd, Tampa, FL 33611"
              className={`w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-400 ${errors.address ? 'border-red-400' : 'border-gray-200'}`}
            />
            {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
          </div>

          {/* Price + City row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Listing Price *</label>
              <input
                type="text"
                value={form.price}
                onChange={(e) => set('price', e.target.value)}
                placeholder="e.g. $1,295,000"
                className={`w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-400 ${errors.price ? 'border-red-400' : 'border-gray-200'}`}
              />
              {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                value={form.city}
                onChange={(e) => set('city', e.target.value)}
                placeholder="e.g. Tampa, Miami, Orlando"
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
          </div>

          {/* Beds / Baths / Sqft */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bedrooms</label>
              <input
                type="number"
                min={0}
                value={form.beds}
                onChange={(e) => set('beds', Number(e.target.value))}
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bathrooms</label>
              <input
                type="number"
                min={0}
                value={form.baths}
                onChange={(e) => set('baths', Number(e.target.value))}
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sq Ft *</label>
              <input
                type="text"
                value={form.sqft}
                onChange={(e) => set('sqft', e.target.value)}
                placeholder="e.g. 2,400"
                className={`w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-400 ${errors.sqft ? 'border-red-400' : 'border-gray-200'}`}
              />
              {errors.sqft && <p className="text-red-500 text-xs mt-1">{errors.sqft}</p>}
            </div>
          </div>

          {/* Type + Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
              <select
                value={form.type}
                onChange={(e) => set('type', e.target.value)}
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-400"
              >
                {PROPERTY_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => set('status', e.target.value)}
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-400"
              >
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Describe the property, key features, neighborhood..."
              className={`w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-400 resize-none ${errors.description ? 'border-red-400' : 'border-gray-200'}`}
            />
            <div className="flex justify-between items-center mt-1">
              {errors.description ? <p className="text-red-500 text-xs">{errors.description}</p> : <span />}
              <p className="text-gray-400 text-xs">{form.description.length}/500</p>
            </div>
          </div>

          {/* Photos */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Photos</label>
            <PhotoUploader
              images={form.images}
              onChange={(imgs) => set('images', imgs)}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-md text-sm text-gray-600 hover:bg-gray-100 cursor-pointer transition-colors whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
            >
              {listing ? 'Save Changes' : 'Add Listing'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
